import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { toast } from "sonner";
import { Camera, Check, Loader, RotateCcw } from "lucide-react";
import { PARKING_PHOTO_ANGLES, parkingPhotosComplete } from "../lib/parkingPhotos";
import { compressImageForUpload, formatUploadError } from "../lib/compressImage";
import { formatInspectionSessionError, formatInspectionSubmitError } from "../lib/bookingErrors";
import { displayOdometerKm } from "../lib/kmUnits";
import { photoEntryFromApi } from "../lib/fileUrl";
import { bindStreamToVideo, formatCameraError, requestCameraStream } from "../lib/cameraStream";
import {
  inspectionAnglesForContext,
  inspectionPhotosComplete,
} from "../lib/inspectionAngles";

const PHASE_LABELS = {
  listing_baseline: "Listing condition scan",
  pickup: "Pickup inspection",
  return: "Return inspection",
  parking_listing: "Parking photos",
};

function anglesForSession(session) {
  if (session?.phase === "parking_listing") return PARKING_PHOTO_ANGLES;
  return inspectionAnglesForContext({
    anglesRequired: session?.angles_required,
    seats: session?.seats,
  });
}

function photosCompleteForSession(session, photos) {
  if (session?.phase === "parking_listing") return parkingPhotosComplete(photos);
  return inspectionPhotosComplete(photos, anglesForSession(session));
}

function withFullUrl(entry) {
  return photoEntryFromApi(entry);
}

export default function MobileInspect() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState({});
  const [activeAngle, setActiveAngle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [odometer, setOdometer] = useState("");
  const [fuel, setFuel] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const startAbortRef = useRef(null);
  const blobRef = useRef(null);
  const pollCount = useRef(0);
  const INITIAL_LOAD_TIMEOUT_MS = 15000;

  const slotAngles = useMemo(() => anglesForSession(session), [session]);
  const angleCount = slotAngles.length;

  const stopCamera = useCallback(() => {
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false);
    setCameraStarting(false);
    setCameraReady(false);
    setCameraError(null);
    setPreviewUrl(null);
    blobRef.current = null;
  }, []);

  const attachStream = useCallback(async (video) => {
    const stream = streamRef.current;
    if (!video || !stream) return;
    setCameraReady(false);
    const ready = await bindStreamToVideo(video, stream);
    setCameraReady(ready);
    if (!ready) toast.error("Camera preview didn't start — try again");
  }, []);

  const setVideoRef = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current && cameraOpen) {
      attachStream(node);
    }
  }, [attachStream, cameraOpen]);

  const startCamera = async () => {
    if (cameraStarting || cameraOpen) return;
    setCameraError(null);
    setCameraReady(false);
    setCameraStarting(true);
    startAbortRef.current?.abort();
    const abort = new AbortController();
    startAbortRef.current = abort;
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Camera not supported");
      const stream = await requestCameraStream({ facingMode: "environment", signal: abort.signal });
      if (abort.signal.aborted) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      setCameraOpen(true);
      if (videoRef.current) await attachStream(videoRef.current);
    } catch (e) {
      if (e?.name !== "AbortError") {
        const msg = formatCameraError(e, { liveCaptureOnly: true });
        setCameraError(msg);
        toast.error(msg);
      }
      setCameraOpen(false);
    } finally {
      if (startAbortRef.current === abort) startAbortRef.current = null;
      setCameraStarting(false);
    }
  };

  useEffect(() => () => stopCamera(), [stopCamera]);

  const load = async ({ isInitial = false } = {}) => {
    try {
      const r = await axios.get(`${API}/inspections/qr-sessions/${token}`, {
        timeout: isInitial ? INITIAL_LOAD_TIMEOUT_MS : undefined,
      });
      setSession(r.data);
      setError(null);
      const draft = {};
      for (const [angle, entry] of Object.entries(r.data.draft_photos || {})) {
        const full = withFullUrl(entry);
        if (full) draft[angle] = full;
      }
      setPhotos(draft);
      const odoKm = displayOdometerKm(r.data);
      if (odoKm != null) setOdometer(String(odoKm));
      if (r.data.fuel_pct != null) setFuel(String(r.data.fuel_pct));
      if (r.data.notes) setNotes(r.data.notes);
      const angles = anglesForSession(r.data);
      const firstMissing = angles.find((a) => !draft[a.id]);
      if (firstMissing) setActiveAngle(firstMissing.id);
      else if (angles[0]) setActiveAngle((prev) => prev || angles[0].id);
    } catch (e) {
      const timedOut = e.code === "ECONNABORTED";
      setError(
        timedOut
          ? "Session took too long to load — check your connection and try again."
          : formatInspectionSessionError(e.response?.data?.detail) || "Session not found or expired"
      );
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    pollCount.current = 0;
    setLoading(true);
    load({ isInitial: true });
  }, [token]);

  useEffect(() => {
    if (!token || session?.status === "submitted") return;
    pollCount.current = 0;
    const id = setInterval(() => {
      if (pollCount.current >= 100) return;
      pollCount.current += 1;
      load();
    }, 3000);
    return () => clearInterval(id);
  }, [token, session?.status]);

  const uploadBlob = async (blob) => {
    if (!blob || !activeAngle) return;
    setBusy(true);
    try {
      const file = new File([blob], `inspect-${activeAngle}.jpg`, { type: blob.type || "image/jpeg" });
      const compressed = await compressImageForUpload(file);
      const fd = new FormData();
      fd.append("file", compressed, compressed.name || `inspect-${activeAngle}.jpg`);
      const r = await axios.post(
        `${API}/inspections/qr-sessions/${token}/upload?angle=${activeAngle}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const entry = withFullUrl(r.data);
      setPhotos((prev) => ({ ...prev, [activeAngle]: entry }));
      toast.success(`${slotAngles.find((a) => a.id === activeAngle)?.label} saved`);
      stopCamera();
      const next = slotAngles.find((a) => !photos[a.id] && a.id !== activeAngle);
      if (next) setActiveAngle(next.id);
    } catch (e) {
      toast.error(formatUploadError(e.response?.data?.detail) || e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) {
      toast.error("Camera is still starting — wait a moment and try again");
      return;
    }
    const scale = Math.min(1, 1920 / Math.max(vw, vh));
    canvas.width = Math.round(vw * scale);
    canvas.height = Math.round(vh * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (blob) {
        blobRef.current = blob;
        setPreviewUrl(URL.createObjectURL(blob));
      }
    }, "image/jpeg", 0.85);
  };

  const confirmCapture = () => {
    if (blobRef.current) uploadBlob(blobRef.current);
  };

  const submit = async () => {
    if (!photosCompleteForSession(session, photos)) {
      toast.error(
        session?.phase === "parking_listing"
          ? "Add at least one parking photo"
          : `Capture all ${angleCount} angles first`,
      );
      return;
    }
    if (session?.phase === "parking_listing") {
      toast.success("Photos saved — return to your browser to submit the listing");
      await load();
      return;
    }
    const isPickup = session?.phase === "pickup";
    const isReturn = session?.phase === "return";
    const isEv = Boolean(session?.is_ev);
    const fuelRequired = isPickup || (isReturn && !isEv);
    const odoVal = odometer ? parseInt(odometer, 10) : NaN;
    if (!Number.isFinite(odoVal) || odoVal < 0) {
      toast.error("Enter the odometer reading in km");
      return;
    }
    if (fuelRequired) {
      const fuelVal = fuel ? parseInt(fuel, 10) : NaN;
      if (!Number.isFinite(fuelVal) || fuelVal < 0 || fuelVal > 100) {
        toast.error(
          isEv
            ? "Enter battery charge % (0–100) before pickup"
            : isPickup
              ? "Enter fuel % (0–100) before pickup"
              : "Enter return fuel % (0–100) — required for ICE vehicles",
        );
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inspections/qr-sessions/${token}/submit`, {
        odometer_km: odoVal,
        fuel_pct: fuel ? parseInt(fuel, 10) : undefined,
        notes,
      });
      toast.success("Inspection submitted — you can close this page");
      await load();
    } catch (e) {
      toast.error(formatInspectionSubmitError(e.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="mobile-inspect-loading">
        <Loader size={24} className="animate-spin text-[var(--zc-text-3)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="mobile-inspect-error">
        <h1 className="zc-headline text-2xl">Session unavailable</h1>
        <p className="text-sm text-[var(--zc-text-2)] mt-3">{error}</p>
        <p className="text-xs text-[var(--zc-text-3)] mt-4">Scan a fresh QR code from the Z-Cars web portal to try again.</p>
      </div>
    );
  }

  if (session?.status === "submitted") {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="mobile-inspect-done">
        <div className="w-14 h-14 rounded-full bg-[var(--zc-success-soft,#dcfce7)] flex items-center justify-center mx-auto">
          <Check size={28} className="text-[var(--zc-success,#16a34a)]" />
        </div>
        <h1 className="zc-headline text-2xl mt-5">Inspection complete</h1>
        <p className="text-sm text-[var(--zc-text-2)] mt-2">
          {PHASE_LABELS[session.phase] || "Inspection"} submitted successfully. Return to your browser to continue.
        </p>
      </div>
    );
  }

  const current = slotAngles.find((a) => a.id === activeAngle) || slotAngles[0];
  const doneCount = slotAngles.filter((a) => photos[a.id]).length;
  const showTripFields = session?.phase === "pickup" || session?.phase === "return";
  const isPickup = session?.phase === "pickup";
  const isReturn = session?.phase === "return";
  const isEv = Boolean(session?.is_ev);
  const fuelRequired = isPickup || (isReturn && !isEv);
  const isListingDraft = session?.phase === "listing_baseline" || session?.phase === "parking_listing";
  const displayLabel = session?.parking_label || session?.vehicle_label;
  const isVehicleInspect = session?.phase !== "parking_listing";

  return (
    <div className="max-w-md mx-auto px-4 py-8 pb-24" data-testid="mobile-inspect-page">
      <div className="eyebrow">Z-Cars mobile scan</div>
      <h1 className="zc-headline text-2xl mt-1">{PHASE_LABELS[session?.phase] || "Condition scan"}</h1>
      {displayLabel && (
        <p className="text-sm text-[var(--zc-text-2)] mt-1">{displayLabel}</p>
      )}

      <div className="flex items-center gap-2 mt-4 mb-4">
        <div className="text-xs text-[var(--zc-text-3)]">
          {doneCount}/{slotAngles.length} {session?.phase === "parking_listing" ? "photos" : "angles"}
        </div>
        <div className="flex-1 h-1.5 bg-[var(--zc-bg-3)] rounded-full overflow-hidden">
          <div className="h-full bg-[var(--zc-accent)] transition-all" style={{ width: `${slotAngles.length ? (doneCount / slotAngles.length) * 100 : 0}%` }} />
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {slotAngles.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => { stopCamera(); setActiveAngle(a.id); }}
            className={"px-2.5 h-7 text-caption rounded-full border font-semibold " +
              (activeAngle === a.id ? "border-[var(--zc-accent)] bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]" :
                photos[a.id] ? "border-[var(--zc-success)] text-[var(--zc-success)]" : "border-[var(--zc-border)] text-[var(--zc-text-3)]")}
          >
            {photos[a.id] ? <Check size={9} className="inline mr-0.5" /> : null}
            {a.label}
          </button>
        ))}
      </div>

      <div className="zc-card p-4">
        <div className="font-semibold text-sm">{current?.label}</div>
        <div className="text-xs text-[var(--zc-text-3)] mt-1">{current?.hint}</div>
        {isVehicleInspect && (
          <p className="text-xs text-[var(--zc-text-3)] mt-2">
            Live capture required — take photos now. Gallery uploads are not accepted.
          </p>
        )}

        {photos[activeAngle] && !cameraOpen && !previewUrl && (
          <div className="mt-3 relative">
            <img src={photos[activeAngle].url} alt="" className="w-full max-h-[min(70vh,480px)] object-contain rounded-lg border border-[var(--zc-border)] bg-[var(--zc-bg-2)]" />
            <button
              type="button"
              onClick={() => {
                stopCamera();
                setPhotos((prev) => { const n = { ...prev }; delete n[activeAngle]; return n; });
              }}
              className="absolute top-2 right-2 text-xs px-2 py-1 bg-white rounded shadow flex items-center gap-1"
            >
              <RotateCcw size={10} /> Retake
            </button>
          </div>
        )}

        {!photos[activeAngle] && !cameraOpen && !cameraStarting && (
          <button
            type="button"
            onClick={startCamera}
            disabled={busy}
            className="mt-4 flex flex-col items-center justify-center gap-2 w-full h-32 rounded-xl border-2 border-dashed border-[var(--zc-border)] active:bg-[var(--zc-bg-2)]"
            data-testid="mobile-inspect-open-camera"
          >
            <Camera size={22} className="text-[var(--zc-accent)]" />
            <span className="text-sm font-semibold">Open camera</span>
          </button>
        )}

        {photos[activeAngle] && !cameraOpen && !previewUrl && (
          <button
            type="button"
            onClick={startCamera}
            className="mt-3 block w-full text-center text-xs text-[var(--zc-accent)] font-semibold"
          >
            Replace with new live photo
          </button>
        )}

        {cameraError && !cameraOpen && !cameraStarting && (
          <p className="text-xs text-[var(--zc-warn)] mt-3">{cameraError}</p>
        )}

        {cameraStarting && (
          <div className="mt-4 p-6 flex flex-col items-center gap-2 text-center">
            <Loader size={22} className="animate-spin text-[var(--zc-accent)]" />
            <p className="text-sm font-semibold">Opening camera…</p>
            <button type="button" onClick={stopCamera} className="text-xs text-[var(--zc-text-3)]">Cancel</button>
          </div>
        )}

        {cameraOpen && !previewUrl && (
          <div className="mt-4">
            <div className="relative rounded-lg overflow-hidden bg-black min-h-[200px] max-h-[min(70vh,480px)]">
              <video
                ref={setVideoRef}
                playsInline
                muted
                autoPlay
                className="w-full h-full max-h-[min(70vh,480px)] min-h-[200px] object-cover"
              />
              {!cameraReady && (
                <div className="absolute inset-0 grid place-items-center bg-black/80 text-white px-4 text-center">
                  <div>
                    <Loader size={22} className="animate-spin mx-auto mb-2" />
                    <p className="text-sm font-semibold">Starting camera preview…</p>
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={captureFrame}
              disabled={busy || !cameraReady}
              className="mt-3 w-full h-11 zc-btn-primary text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="mobile-inspect-capture"
            >
              {busy ? <Loader size={14} className="animate-spin" /> : <Camera size={14} />}
              {cameraReady ? `Capture ${current?.label}` : "Waiting for camera…"}
            </button>
            <button type="button" onClick={stopCamera} className="mt-2 w-full h-9 zc-btn-ghost text-xs">Cancel</button>
          </div>
        )}

        {previewUrl && (
          <div className="mt-4">
            <img src={previewUrl} alt="" className="w-full rounded-lg max-h-[min(70vh,480px)] object-contain bg-[var(--zc-bg-2)]" />
            <div className="flex gap-2 mt-3">
              <button type="button" onClick={confirmCapture} disabled={busy} className="flex-1 h-10 zc-btn-primary text-sm flex items-center justify-center gap-2">
                {busy ? <Loader size={14} className="animate-spin" /> : <Check size={14} />} Use photo
              </button>
              <button type="button" onClick={() => { setPreviewUrl(null); blobRef.current = null; }} className="h-10 px-4 zc-btn-ghost text-sm flex items-center gap-1">
                <RotateCcw size={12} /> Retake
              </button>
            </div>
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>

      {showTripFields && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-xs text-[var(--zc-text-3)]">Odometer (km){isPickup ? " *" : ""}</label>
            <input className="zc-input w-full h-10 px-3 text-sm mt-1" value={odometer} onChange={(e) => setOdometer(e.target.value)} inputMode="numeric" placeholder="e.g. 20000" required={isPickup} />
            <div className="zc-meta mt-1">Clear odometer reading in km</div>
          </div>
          <div>
            <label className="text-xs text-[var(--zc-text-3)]">
              {isEv ? "Battery charge %" : "Fuel %"}
              {fuelRequired ? " *" : ""}
            </label>
            <input
              className="zc-input w-full h-10 px-3 text-sm mt-1"
              value={fuel}
              onChange={e => setFuel(e.target.value)}
              inputMode="numeric"
              placeholder="75"
              required={fuelRequired}
              data-testid="mobile-inspect-fuel"
            />
          </div>
        </div>
      )}

      <textarea
        className="zc-input w-full p-3 text-sm mt-3 h-16"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Optional notes…"
      />

      {isListingDraft ? (
        <div
          className="fixed inset-x-0 z-40 px-4 pt-3 bg-[var(--zc-bg)] border-t border-[var(--zc-border)]"
          style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          <p className="text-xs text-center text-[var(--zc-text-2)] max-w-md mx-auto">
            {photosCompleteForSession(session, photos)
              ? session?.phase === "parking_listing"
                ? "Photos captured — return to your browser to submit the parking listing."
                : "All angles captured — return to your browser to submit the listing."
              : session?.phase === "parking_listing"
                ? "Add at least one photo, then finish submitting on your computer."
                : `Capture all ${angleCount} angles, then finish submitting on your computer.`}
          </p>
        </div>
      ) : (
        <div
          className="fixed inset-x-0 z-40 px-4"
          style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !photosCompleteForSession(session, photos)}
          className="w-full max-w-md mx-auto h-12 zc-btn-primary text-sm flex items-center justify-center gap-2 shadow-lg"
          data-testid="mobile-inspect-submit"
        >
          {submitting ? <Loader size={14} className="animate-spin" /> : <Check size={14} />}
          Submit inspection
        </button>
        </div>
      )}
    </div>
  );
}
